import React, { useCallback, useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { useTablePropsInfo } from '@op/hooks/common';
import { useSummaryListColumns } from '@op/components/TableCommonHook/hooks/useSummaryListColumns';
import ReportAccessPanel from '@op/components/ReportPermission';
import { useOpenReportPermission } from '@op/hooks/useOpenReportPermission';
import { DBO_GAME } from '@op/utils/format';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useRepeatSummaryList } from '@op/pages/AIAnalysisHub/CustomAISummary/hooks';
import {
  useAiReportNewTag,
  useDownloadSummaryItem,
  usePreviewSummaryItem,
  RecordsType,
} from '@op/pages/AIAnalysisHub/hooks';
import { requestAiTaskCenterRepeat } from '@op/services';
import SummaryModal, {
  useModalData,
} from '@op/pages/AIAnalysisHub/CustomAISummary/component/SummaryModal';
import useLanguage from '@op/hooks/useLanguage';

type TabType =
  | 'all'
  | 'common'
  | 'repeat_summary_task'
  | 'keyword'
  | 'url'
  | 'topic'
  | 'keyword_topic';

interface ISubscriptionsTableProps {
  filter: any;
  keys: string[];
  gameInfoMap?: Record<string, any>;
  deleteToggleState: () => void;
  tab: TabType;
  urlTab?: TabType;
}

/* eslint-disable react/prop-types */
export default function SubscriptionsTable({
  filter,
  keys,
  gameInfoMap,
  deleteToggleState,
  tab,
  urlTab,
}: ISubscriptionsTableProps) {
  const { formatMessage } = useLanguage();
  const [gameInfo, setGameInfo] = useState({});
  const { sortBy, setSortBy, pageInfo, changePageInfo, sortFilter } =
    useTablePropsInfo({
      defaultPageInfo: { page: 1, page_size: 10 },
      defaultSortBy: {
        by: 'create_time',
        order: 'desc',
      },
    });

  const realFilter = useMemo(() => {
    const otherInfo: any = {};
    if (tab !== 'repeat_summary_task') {
      otherInfo.category = [tab];
    }

    return {
      ...filter,
      ...sortFilter,
      ...pageInfo,
      ...otherInfo,
    };
  }, [filter, sortFilter, pageInfo, tab]);

  const isKeywordTab = ['keyword', 'topic', 'keyword_topic', 'repeat_summary_task'].includes(tab);

  const { loading, deleteSummary, stopSummary, resumeSummary, data, total, requestSummaryList } =
    useRepeatSummaryList({
      filter: realFilter,
      clearPageInfo: changePageInfo,
      needRequest: true,
      requestFunc: requestAiTaskCenterRepeat,
      isKeywordTopic: isKeywordTab,
      type: tab,
    });

  const {
    loading: newTagLoading,
    noNewTagsReport,
    setReportView,
  } = useAiReportNewTag({
    data,
    type: ['keyword', 'topic'].includes(tab)
      ? 'repeat_keyword_analysis_task'
      : 'repeat_summary_task',
  });

  const downloadSummaryMap = useDownloadSummaryItem({
    isSubscription: true,
  });
  const previewSummaryMap = usePreviewSummaryItem({
    isSubscription: true,
  });

  const { modalProps, modalVisible, modalType, toggleModal, category } =
    useModalData();

  const handlePermissionSaved = useCallback(() => {
    requestSummaryList();
    deleteToggleState();
  }, [deleteToggleState, requestSummaryList]);

  const { open: openPermissionPanel, openByTarget, visible, target, close } =
    useOpenReportPermission();

  const { columns, sortColumns } = useSummaryListColumns({
    clickDelete: async (records: RecordsType) => {
      buriedBtnPoint('Delete', 'Subscriptions Summary');
      await deleteSummary(records);
      deleteToggleState();
    },
    clickPreview: (records: RecordsType) => {
      if (!noNewTagsReport[records.id]) {
        setReportView(String(records.id));
      }
      const gameInfo = gameInfoMap?.[records.game_id];
      const params = {
        ...records,
        gameInfo: DBO_GAME.params(gameInfo),
      };
      const owner = filter.owner;
      if (['keyword', 'topic', 'keyword_topic'].includes(tab)) {
        previewSummaryMap[tab as 'topic']?.(params, urlTab, tab, owner);
        return;
      }
      previewSummaryMap[tab as 'topic']?.(params, urlTab, owner);
    },
    clickDownload: (records: RecordsType) => {
      const gameInfo = gameInfoMap?.[records.game_id];
      const params = {
        ...records,
        gameInfo: DBO_GAME.params(gameInfo),
      };
      const owner = filter.owner;
      if (['keyword', 'topic', 'keyword_topic', 'url'].includes(tab)) {
        downloadSummaryMap[tab as 'topic']?.(params, urlTab, owner);
        return;
      }
      downloadSummaryMap[tab as 'topic']?.(params);
    },
    ...(isKeywordTab
      ? {
        clickStop: (records: RecordsType) => {
          const gameInfo = gameInfoMap?.[records.game_id];
          buriedBtnPoint('Stop', 'Subscriptions Summary');
          stopSummary({ ...records, gameInfo });
        },
        // clickResume: (records: RecordsType) => {
        //   const gameInfo = gameInfoMap?.[records.game_id];
        //   buriedBtnPoint('Resume', 'Subscriptions Summary');
        //   resumeSummary({ ...records, gameInfo });
        // },
      }
      : {}),
    keys,
    toggleModal: (
      type: string,
      props: { records: RecordsType },
      tabParam = '',
    ) => {
      toggleModal(type, props, tabParam);
      const { records } = props;
      const gameInfo = gameInfoMap?.[records.game_id];
      setGameInfo(gameInfo);
    },
    noNewTagsReport,
    newTagLoading,
    gameInfoMap,
    tab,
    onOpenPermissionPanel: openPermissionPanel,
  });

  return (
    <>
      <TableCommonHook
        columns={columns}
        sortColumns={sortColumns}
        records={data}
        total={total}
        loading={loading}
        height={506}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        sortInfo={[sortBy, setSortBy]}
      />
      <ReportAccessPanel
        visible={visible}
        target={target}
        onClose={close}
        onSaved={handlePermissionSaved}
        notificationMode="list"
        onPermissionLinkClick={openByTarget}
      />
      {modalVisible && (
        <SummaryModal
          visible={modalVisible}
          modalType={modalType}
          toggleVisible={toggleModal}
          formatMessage={formatMessage}
          request={() => {
            requestSummaryList();
          }}
          category={category}
          gameInfo={gameInfo}
          {...modalProps}
        />
      )}
    </>
  );
}
